/**
 * Theme System Exports
 * All available themes for the Daggerheart character sheet application
 */

import { Theme } from '../tokens';
import { darkTheme } from './dark';
import { lightTheme } from './light';

// Available themes array
export const availableThemes: Theme[] = [
  darkTheme,
  lightTheme,
];

// Theme lookup by name
export const themesByName: Record<string, Theme> = {
  dark: darkTheme,
  light: lightTheme,
};

// Default theme
export const defaultTheme = darkTheme;

// Export individual themes
export { darkTheme, lightTheme };

// Helper functions
export const getThemeByName = (name: string): Theme | undefined => {
  return themesByName[name];
};

export const isValidThemeName = (name: string): boolean => {
  return name in themesByName;
};

export const getThemeNames = (): string[] => {
  return Object.keys(themesByName);
};

export const getDarkThemes = (): Theme[] => {
  return availableThemes.filter(theme => theme.metadata.isDark);
};

export const getLightThemes = (): Theme[] => {
  return availableThemes.filter(theme => !theme.metadata.isDark);
};

// Theme validation
export const validateTheme = (theme: Theme): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  // Basic metadata validation
  if (!theme.metadata) {
    errors.push('Theme metadata is required');
  } else {
    if (!theme.metadata.name) errors.push('Theme name is required');
    if (!theme.metadata.displayName) errors.push('Theme displayName is required');
    if (!theme.metadata.version) errors.push('Theme version is required');
    if (typeof theme.metadata.isDark !== 'boolean') errors.push('Theme isDark must be boolean');
  }
  
  // Token structure validation
  if (!theme.tokens) {
    errors.push('Theme tokens are required');
  } else {
    if (!theme.tokens.primitive) errors.push('Primitive tokens are required');
    if (!theme.tokens.semantic) errors.push('Semantic tokens are required');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Export types for convenience
export type { Theme } from '../tokens';
